import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Input, InputNumber, Select, DatePicker,
  Button, Card, Row, Col, Typography, message, Divider,
  Upload, Modal, Space, Checkbox, Tag, Popconfirm } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import {
  ArrowLeftOutlined, SaveOutlined, PlusOutlined, LoadingOutlined,
  BarChartOutlined, ExperimentOutlined, TrophyOutlined, ApartmentOutlined,
  CalendarOutlined, HeartOutlined, UsergroupAddOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { animalService, categoriaService, razaService, cabanaService, eventoService, clienteService, imagenService } from '../../services/api';
import FotosAnimal, { FotosPendientes, conImagenActualizada } from '../../components/FotosAnimal';
import { useAuth } from '../../context/AuthContext';
import { esAdminPlataforma } from '../../utils/rutasAcceso';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Espejo de `Genealogia.CAMPOS` en el backend. Los tatarabuelos se sacaron el
// 2026-08-21 junto con la mudanza de la genealogía a la tabla `animal`: sobre
// 584 animales cargados a mano durante meses tenían cero datos.
const GENEALOGIA_CAMPOS = [
  'nom_padre', 'nom_madre',
  'abuelo_p', 'abuela_p', 'abuelo_m', 'abuela_m',
  'bisabuelo_p', 'bisabuela_p', 'bisabuelo_m', 'bisabuela_m',
];

// Los cinco del modal de Servicio. Todos viven en `CAMPOS_EDITABLES_ANIMAL`
// (animal_routes.py), asi que su "Listo" puede persistir igual que el de los
// grupos geneticos. (Las crias ya no viven en este form: desde el 2026-10-21 son
// varias por madre y se cargan en planilla en la solapa «Crías» de la ficha.)
const SERVICIO_CAMPOS = ['tipo_servicio', 'toro_servicio', 'estado_servicio', 'fecha_servicio', 'fecha_parto'];

// Nombre legible de los campos obligatorios, para el aviso de validación.
const ETIQUETAS_CAMPOS = {
  nom_animal: 'Nombre',
  rp_animal:  'RP',
  registro:   'Registro',
  fecha_nac:  'Fecha Nacimiento',
  // Obligatorio al CREAR, y solo para el admin de plataforma (es el unico que
  // ve el select). Sin esta entrada, ese caso caia en el mensaje generico
  // "Hay campos obligatorios sin completar" — justo lo que onFinishFailed
  // existe para evitar.
  id_clientes: 'Cliente',
};

// ── Campos de un grupo genético (deps / pres / rank) ─────────────────
const FILAS_GENETICAS = [
  { key: 'nacer',  label: 'Nacimiento' },
  { key: 'dest',   label: 'Destete' },
  { key: 'apmat',  label: 'Ap. Materna' },
  { key: 'lyc',    label: 'LyC' },
  { key: 'final',  label: 'Final' },
  { key: 'ce',     label: 'CE' },
  { key: 'aob',    label: 'AOB' },     // Área de Ojo de Bife
  { key: 'gd',     label: 'GD' },      // Grasa Dorsal (mm)
  { key: 'gc',     label: 'GC' },      // Grasa de Cadera (mm)
  { key: 'porcgi', label: '%GI' },     // % Grasa Intramuscular
];

/**
 * El nombre de FANTASIA, para el menu del catalogo de genetica y las tarjetas.
 *
 * `nom_animal` es el nombre de pedigri completo y no entra en un menu:
 *
 *     PAYESERO MARCABOTE 51M PAYÉ 21570 MÁGICO T/E
 *
 * NO SE PUEDE DERIVAR, y por eso hay un campo. Se probaron heuristicas contra
 * los datos reales: en "PAYESERO MARCABOTE 51M..." el nombre esta PRIMERO, en
 * "PILAGÁS 30893 MAGO..." esta DESPUES del numero, y en "MARCABOTE 389M
 * POMELO..." esta TERCERO. "Primera palabra" acierta la mitad; "despues del
 * primer numero" acierta la otra mitad. El orden depende de quien cargo la
 * planilla y de la asociacion.
 *
 * LAS SUGERENCIAS SE TOCAN, no vienen precargadas. Una sugerencia que acierta
 * la mitad de las veces es PEOR que ninguna: se acepta sin mirar, y termina
 * habiendo tres toros que en el menu dicen "MARCABOTE".
 *
 * A nivel de modulo y no dentro del render de `AnimalForm`: definirlo adentro
 * hace que React lo vea como un componente nuevo en cada tecla y remonte el
 * input, que pierde el foco mientras se escribe.
 */
const NombreCortoField = ({ form }) => {
  const nombre = Form.useWatch('nom_animal', form) || '';

  // Las dos lecturas posibles del nombre de pedigri. Se ofrecen LAS DOS
  // justamente porque no se sabe cual aplica a este animal.
  const palabras = nombre.trim().split(/\s+/).filter(Boolean);
  const primera = palabras[0];
  const indiceNumero = palabras.findIndex((p) => /\d/.test(p));
  const trasNumero = indiceNumero >= 0 ? palabras[indiceNumero + 1] : undefined;

  const sugerencias = [...new Set([primera, trasNumero])]
    .filter((p) => p && p.length > 2 && !/\d/.test(p));

  return (
    <Form.Item
      name="nombre_corto"
      label="Nombre corto"
      extra={
        <span>
          Con el que se lo conoce. Es lo que sale en el menú y las tarjetas del
          catálogo de genética; si lo dejás vacío se usa el nombre completo.
          {sugerencias.length > 0 && (
            <>
              {' '}Del nombre salen:{' '}
              {sugerencias.map((sug) => (
                <Tag
                  key={sug}
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => form.setFieldValue('nombre_corto', sug)}
                >
                  {sug}
                </Tag>
              ))}
            </>
          )}
        </span>
      }
    >
      <Input placeholder="Ej: PAYESERO" maxLength={80} />
    </Form.Item>
  );
};


const AnimalForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [razas, setRazas] = useState([]);
  // Cabañas participantes: de cuál VIENE el animal, para los remates de varias
  // cabañas. Es procedencia, no dueño — el dueño sigue siendo el tenant.
  const [cabanas, setCabanas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [clientes, setClientes] = useState([]);
  // Los cuatro selects cargan de API: sin `loading` el usuario abre el
  // dropdown vacío y tiene que reabrirlo cuando la respuesta llega.
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [loadingRazas, setLoadingRazas] = useState(false);
  const [loadingCabanas, setLoadingCabanas] = useState(false);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  // TODAS las fotos del animal, en el orden del backend (por id). La card las
  // lista completas: antes guardaba solo la portada, y con varias fotos por
  // animal una card de una sola foto con un tachito al lado borra la portada
  // y promueve a la siguiente sin que el productor haya visto las demas.
  const [imagenes, setImagenes] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  // El id de la foto que se esta borrando, para el spinner de SU tachito.
  const [eliminandoImg, setEliminandoImg] = useState(null);
  // Los archivos a subir cuando el animal se cree. En PLURAL desde que el input
  // acepta varios: antes era uno y elegir cinco fotos en el alta guardaba solo
  // la ultima, en silencio.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [depsVisible, setDepsVisible] = useState(false);
  const [presVisible, setPresVisible] = useState(false);
  const [rankVisible, setRankVisible] = useState(false);
  const [genealVisible, setGenealVisible] = useState(false);
  const [servicioVisible, setServicioVisible] = useState(false);
  const [sexo, setSexo] = useState(null);
  /* El remate en el que está el animal, para el botón «Sacar del remate» de
   * la cabecera (usuario, 2026-10-28: que la invitada pueda sacarlo desde la
   * ficha y no sólo tildándolo en el listado). Viene del backend
   * (`nombre_evento`) porque sin `asignar_animales_evento` la lista de
   * remates no se carga y no habría de dónde sacar el nombre. */
  const [remate, setRemate] = useState(null);
  const [sacando, setSacando] = useState(false);
  // Navegación prev/next entre animales del productor (orden por corral, igual
  // que el listado). Solo en modo edición.
  const [vecinos, setVecinos] = useState({ prev: null, next: null });
  // Qué hacer después de guardar: 'quedarse' (sigue en el animal) o 'siguiente'.
  // Lo setea el botón presionado justo antes del submit.
  const accionGuardarRef = useRef('quedarse');
  // EL ID QUE SE ESTA EDITANDO AHORA. El efecto de carga usa una bandera por
  // corrida (`estaCancelado`), pero la subida arranca por un click y puede
  // terminar despues de que el productor paso al animal siguiente con la
  // flecha — el componente NO se desmonta, solo le cambia el `:id`. Sin esto,
  // las fotos del animal anterior se dibujan sobre la ficha del nuevo: animal
  // correcto, fotos ajenas. Es el mismo pozo que el comentario de
  // `fetchImagenes` describe, y con varias fotos hay mas tiempo para caerse
  // en el. El borrado tambien pasa por aca, por el mismo motivo.
  const idActualRef = useRef(id);
  useEffect(() => { idActualRef.current = id; }, [id]);

  const isEditing = !!id;
  const { tieneRecurso, user } = useAuth();

  // `fotos_videos` y NO el recurso de esta pantalla: `POST /imagenes/upload-animal`
  // va gateado con ese. Sin la guarda el productor ve el boton, elige cinco
  // fotos y se come un 403 —o peor, en el alta: el animal se crea y despues un
  // aviso le dice que las fotos no subieron—. Mismo caso que ya documenta
  // AGENTS.md para la columna «Presentacion» del catalogo.
  const puedeFotos = tieneRecurso('fotos_videos');
  const puedeCatalogo = tieneRecurso('catalogo_genetica');

  // Editando, el backend ignora id_clientes (cross-tenant). Solo el admin de
  // plataforma lo define, y unicamente al crear.
  const clienteBloqueado = isEditing;

  // Carga los animales del productor (orden por corral, como el listado) para
  // resolver el anterior/siguiente del que se está editando.
  useEffect(() => {
    if (!isEditing) return;
    // Limpieza ANTES del await, no despues. "Guardar y siguiente" navega sin
    // desmontar, asi que entre el navigate y la respuesta —un round-trip a otra
    // region— `vecinos` seguia siendo el del animal ANTERIOR. En esa ventana
    // `vecinos.next` apunta al animal en el que ya estas: apretar "Guardar y
    // siguiente" guardaba y navegaba a la ruta actual, y el usuario veia el
    // guardado exitoso y la pantalla clavada.
    setVecinos({ prev: null, next: null });
    let cancelado = false;
    (async () => {
      try {
        // vista=ids -> `{ ids, total }` y nada mas. Antes esto pedia la ficha
        // completa de hasta 1000 animales (vista=venta, con imagenes y ventas)
        // para quedarse solo con `id_animal`: cargaba el camion entero para
        // contar orejas. OJO: va sin los filtros del listado, asi que la
        // navegacion recorre TODOS los animales del productor, no el
        // subconjunto filtrado.
        const res = await animalService.getIds();
        const { ids = [], total = 0 } = res.data || {};
        const idx = ids.indexOf(Number(id));
        if (cancelado) return;

        // Hoy este aviso NO puede dispararse: vista=ids no pagina, asi que
        // `total` siempre es igual a `ids.length`. Se queda igual, y a proposito.
        //
        // El aviso existe porque la version anterior truncaba en silencio: la
        // lista volvia cortada, la flecha "siguiente" moria y el usuario leia
        // eso como "este es el ultimo animal del padron". Un 200 con una
        // respuesta incompleta es la peor clase de falla, porque no deja rastro.
        //
        // Si manana alguien le pone un tope a la rama `ids` —por peso, por
        // timeout, por lo que sea— este chequeo lo detecta solo. Borrarlo por
        // "codigo muerto" seria sacar el detector de humo porque no hay fuego.
        if (total > ids.length) {
          message.warning(
            'Tenés más animales de los que entran en la navegación. '
            + 'Usá el listado para moverte entre ellos.'
          );
        }

        // El `else` con `{ prev: null, next: null }` NO es defensivo: "Guardar
        // y siguiente" navega a otro `:id` SIN desmontar el componente, asi que
        // sin reset `vecinos` conserva los del animal ANTERIOR. Se veria el
        // aviso de arriba ("usa el listado") y AL LADO las flechas habilitadas
        // apuntando a otro lado — peor que no avisar nada. Es el mismo motivo
        // por el que `fetchAnimal` resetea `imagenes` y `sexo`; `vecinos` era
        // el unico que faltaba.
        setVecinos(idx !== -1
          ? {
              prev: idx > 0 ? ids[idx - 1] : null,
              next: idx < ids.length - 1 ? ids[idx + 1] : null,
            }
          : { prev: null, next: null });
      } catch (err) {
        // Sin aviso, las flechas quedan deshabilitadas y no hay forma de saber
        // si es que este es el último animal o si la request murió.
        if (!cancelado) {
          message.error(err.response?.data?.error || 'No se pudo cargar la navegación entre animales');
        }
      }
    })();
    return () => { cancelado = true; };
  }, [id, isEditing]);

  // Catalogos: no dependen del animal, asi que se piden UNA vez. Estaban en el
  // mismo effect que `fetchAnimal`, con deps `[id]`: "Guardar y siguiente" 40
  // veces disparaba 200 requests de listas que no habian cambiado.
  useEffect(() => {
    fetchCategorias();
    fetchRazas();
    // `GET /cabanas/activas` pide `ver_cabanas`, otra entrada del catalogo que
    // se togglea aparte de `crud_animales`. Un plan con una y sin la otra es
    // posible, y sin este gate ese usuario se come un `message.error` rojo en
    // CADA alta y CADA edicion, por una feature que su plan no tiene. El campo
    // ya degrada solo (`cabanas.length > 0`), asi que no se pierde nada: lo
    // unico que se evita es ruido que no puede resolver. Misma convencion que
    // `puedeAsignarCabana` en AnimalesList.jsx.
    if (tieneRecurso('ver_cabanas')) fetchCabanas();
    // Mismo caso EXACTO que las cabañas: `/eventos/lista-corta` pide
    // `ver_eventos` (evento_routes.py:160). Si la regla se escribe, se aplica a
    // los dos, o el que quede sin gatear sigue tirando el mismo toast rojo
    // inutil en cada alta y cada edicion.
    // Y `asignar_animales_evento` además de `ver_eventos`: la lista corta trae
    // también los remates a los que me INVITARON, y una cabaña invitada no
    // asigna animales a ningún remate (el backend lo rechaza con EVENTO_AJENO;
    // eso es del organizador). Sin el recurso el campo no se dibuja y el
    // animal conserva el remate que tenga.
    if (tieneRecurso('ver_eventos') && tieneRecurso('asignar_animales_evento')) fetchEventos();
    // La lista de clientes es la cartera entera de Puja: solo la pide quien
    // tiene que elegir de ahi. Un productor no la necesita —el backend le pone
    // el dueño solo— y traerla era, ademas de una request al pedo, listarle
    // todos los tenants de la plataforma a un admin de cabaña.
    if (esAdminPlataforma(user)) fetchClientes();
    // `tieneRecurso` NO va en las deps, y es una decision, no un olvido:
    // agregarla re-dispara los cinco fetch en cada cambio de identidad de la
    // funcion, que es exactamente lo que este effect vino a evitar. Es seguro
    // porque los recursos estan cargados ANTES del mount (AuthContext hace
    // `await cargarRecursos()` antes de prender `isAuthenticated`, y
    // ProtectedRoute no renderiza mientras `loading`). Queda un hueco chico: si
    // `recargarRecursos()` corre con este form abierto, el closure es viejo y
    // el campo Cabaña no aparece hasta remontar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El animal si depende de `id`: esto tiene que refrescar en cada navegacion.
  // El cleanup marca cancelado el fetch anterior antes de que arranque el
  // nuevo, asi que una respuesta que llega tarde ya no puede pisar a la que
  // corresponde. Mismo patron que el effect de vecinos de arriba.
  useEffect(() => {
    if (!isEditing) return;
    let cancelado = false;
    const estaCancelado = () => cancelado;
    fetchAnimal(estaCancelado);
    fetchImagenes(estaCancelado);
    return () => { cancelado = true; };
  }, [id, isEditing]);

  // Misma carrera que `fetchAnimal`, con otro sintoma: las fotos que llegan
  // tarde se pegan sobre la ficha del animal siguiente. Animal correcto, fotos
  // ajenas.
  const fetchImagenes = async (estaCancelado = () => false) => {
    try {
      const res = await imagenService.getByAnimal(id);
      if (estaCancelado()) return;
      // La lista ENTERA y como la manda el backend, sin elegir nada aca: cual
      // es la portada lo dice cada fila con su switch, y `FotosAnimal` aplica
      // el mismo criterio que el backend cuando hay que mostrarlo sin switch.
      setImagenes(res.data?.imagenes || []);
    } catch (err) {
      // OJO: aca NO se excluye el 404, y el comentario anterior estaba MAL.
      // Decia "un 404 es normal, el animal todavia no tiene foto", pero
      // `GET /imagenes/animal/:id` devuelve **200 con lista vacia** cuando no
      // hay fotos (imagen_routes.py). El unico 404 que ese endpoint puede tirar
      // es el de `ensure_owner`: el animal NO EXISTE o NO ES TUYO — o sea
      // justo el error que hay que mostrar, y que la exclusion se tragaba.
      if (!estaCancelado()) {
        message.error(err.response?.data?.error || 'Error al cargar la imagen del animal');
      }
    }
  };

  // `beforeUpload` de antd se dispara UNA VEZ POR ARCHIVO, pero su segundo
  // argumento trae la tanda COMPLETA. Se actua solo en la primera llamada y con
  // la lista entera: si no, elegir cinco fotos dispara cinco requests, cinco
  // mensajes de exito y cinco re-renders para una sola accion del productor.
  const handleUploadImagen = async (file, fileList) => {
    const tanda = fileList && fileList.length ? fileList : [file];
    if (tanda.indexOf(file) > 0) return false;

    if (!isEditing) {
      // En el alta se ACUMULAN: elegir tres y despues dos mas son cinco, igual
      // que si se hubieran elegido juntas. Antes cada tanda pisaba la anterior
      // y solo se veia la primera foto. La lista de pendientes las muestra
      // todas, con una cruz para sacar una. Los blobs de preview los maneja
      // `FotosPendientes`, que es quien los dibuja.
      setPendingFiles((prev) => [...prev, ...tanda]);
      return false;
    }
    setUploadingImg(true);
    try {
      const idDeLaSubida = id;
      const res = await imagenService.uploadAnimal(idDeLaSubida, tanda);
      // La preview se refresca desde la BASE y no con lo que acaba de subir:
      // la portada sigue siendo la que estaba marcada, y una foto nueva no la
      // reemplaza por haber llegado ultima.
      //
      // Con el mismo guard que el resto del archivo: si mientras subia se paso
      // a otro animal, esta respuesta ya no va a esta pantalla.
      await fetchImagenes(() => idActualRef.current !== idDeLaSubida);
      if (idActualRef.current !== idDeLaSubida) return false;
      const cuantas = res.data?.total ?? 1;
      const rechazadas = res.data?.rechazadas || [];
      message.success(cuantas === 1 ? 'Foto cargada' : `${cuantas} fotos cargadas`);
      // Las que no entraron se dicen APARTE y con su motivo. Un "5 fotos
      // cargadas" cuando entraron 3 es peor que no avisar nada.
      if (rechazadas.length) {
        const nombres = rechazadas.map((r) => r.archivo).join(', ');
        message.warning(rechazadas.length === 1
          ? `1 no se pudo subir: ${nombres}`
          : `${rechazadas.length} no se pudieron subir: ${nombres}`);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al subir la foto');
    } finally {
      setUploadingImg(false);
    }
    return false;
  };

  // Un control de presentacion (portada, carrusel, encuadre) devolvio la foto
  // actualizada: se parchea la lista en memoria en vez de recargar. La regla
  // de "marcar una portada desmarca la anterior" vive con la lista.
  const handleCambioImagen = (actualizada) => {
    setImagenes((prev) => conImagenActualizada(prev, actualizada));
  };

  // Borrar una foto. `DELETE /imagenes/:id` borra el archivo y sus versiones;
  // la fila se saca de la lista con lo que dijo el servidor, no antes: un
  // tachito optimista que despues falla deja una foto "borrada" que sigue
  // publicada. Mismo guard de `idActualRef` que la subida: si mientras
  // borraba se paso a otro animal, esta respuesta ya no va a esta pantalla.
  const handleEliminarImagen = async (imagen) => {
    const idDelAnimal = id;
    setEliminandoImg(imagen.id_imagen);
    try {
      await imagenService.delete(imagen.id_imagen);
      if (idActualRef.current !== idDelAnimal) return;
      setImagenes((prev) => prev.filter((img) => img.id_imagen !== imagen.id_imagen));
      message.success('Foto eliminada');
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos eliminar la foto');
    } finally {
      setEliminandoImg(null);
    }
  };

  // Sacar una foto de la tanda del alta. Sin confirmacion: no se subio nada.
  const quitarPendiente = (indice) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== indice));
  };

  // `estaCancelado` NO es paranoia defensiva: sin el, dos respuestas que
  // vuelven fuera de orden escriben los datos de UN animal sobre OTRO.
  //
  // 1. Se aprieta "siguiente": arranca el fetch del animal 1.
  // 2. Se aprieta de nuevo antes de que llegue: arranca el del animal 2.
  // 3. Llega la 2: el form muestra el animal 2.
  // 4. Llega la 1, TARDE: pisa todo con los datos del animal 1.
  // La URL dice /animales/2/editar, el form muestra el 1, y al guardar se
  // escribe el payload del 1 sobre el 2.
  //
  // El reset de arriba NO cubre esto: protege la navegacion (no ver datos
  // viejos mientras carga), no el orden de llegada. Son dos problemas
  // distintos por la misma puerta. Y no es teorico: la base esta en otra
  // region, cada request es un round-trip, y son dos clicks seguidos.
  const fetchAnimal = async (estaCancelado = () => false) => {
    setLoading(true);
    // Reset del estado que depende del animal. "Guardar y siguiente" cambia el
    // :id sin desmontar el componente, así que sin esto quedan los datos del
    // anterior: la foto de portada muestra la que no es.
    setImagenes([]);
    setSexo(null);
    setRemate(null);
    try {
      const response = await animalService.getById(id);
      if (estaCancelado()) return;
      const animal = response.data;

      // Convertir fecha para DatePicker
      setSexo(animal.sexo ?? null);
      setRemate(animal.id_evento ? { id: animal.id_evento, nombre: animal.nombre_evento } : null);
      if (estaCancelado()) return;
      form.setFieldsValue({
        ...animal,
        id_raza: animal.id_raza ?? null,
        id_cabana: animal.id_cabana ?? null,
        fecha_nac: animal.fecha_nac ? dayjs(animal.fecha_nac) : null,
        tipo_servicio:  animal.servicio?.tipo ?? null,
        toro_servicio:  animal.servicio?.toro ?? null,
        estado_servicio: animal.servicio?.estado ?? null,
        // Texto libre (un período de servicio), no una fecha: va tal cual.
        fecha_servicio: animal.servicio?.fecha_servicio ?? null,
        fecha_parto: animal.servicio?.fecha_parto ? dayjs(animal.servicio.fecha_parto) : null,
        // DEPs (aplanamos el objeto anidado)
        nacer_deps:  animal.deps?.nacer ?? null,
        dest_deps:   animal.deps?.destete ?? null,
        apmat_deps:  animal.deps?.ap_materna ?? null,
        lyc_deps:    animal.deps?.lyc ?? null,
        final_deps:  animal.deps?.final ?? null,
        ce_deps:     animal.deps?.ce ?? null,
        aob_deps:    animal.deps?.aob ?? null,
        gd_deps:     animal.deps?.gd ?? null,
        gc_deps:     animal.deps?.gc ?? null,
        porcgi_deps: animal.deps?.porcgi ?? null,
        // Precisión
        nacer_pres:  animal.precision?.nacer ?? null,
        dest_pres:   animal.precision?.destete ?? null,
        apmat_pres:  animal.precision?.ap_materna ?? null,
        lyc_pres:    animal.precision?.lyc ?? null,
        final_pres:  animal.precision?.final ?? null,
        ce_pres:     animal.precision?.ce ?? null,
        aob_pres:    animal.precision?.aob ?? null,
        gd_pres:     animal.precision?.gd ?? null,
        gc_pres:     animal.precision?.gc ?? null,
        porcgi_pres: animal.precision?.porcgi ?? null,
        // Ranking
        nacer_rank:  animal.ranking?.nacer ?? null,
        dest_rank:   animal.ranking?.destete ?? null,
        apmat_rank:  animal.ranking?.ap_materna ?? null,
        lyc_rank:    animal.ranking?.lyc ?? null,
        final_rank:  animal.ranking?.final ?? null,
        ce_rank:     animal.ranking?.ce ?? null,
        aob_rank:    animal.ranking?.aob ?? null,
        gd_rank:     animal.ranking?.gd ?? null,
        gc_rank:     animal.ranking?.gc ?? null,
        porcgi_rank: animal.ranking?.porcgi ?? null,
        // Genealogía (aplanamos el objeto anidado)
        nom_padre:      animal.genealogia?.padres?.padre ?? null,
        nom_madre:      animal.genealogia?.padres?.madre ?? null,
        abuelo_p:       animal.genealogia?.abuelos?.paterno?.abuelo ?? null,
        abuela_p:       animal.genealogia?.abuelos?.paterno?.abuela ?? null,
        abuelo_m:       animal.genealogia?.abuelos?.materno?.abuelo ?? null,
        abuela_m:       animal.genealogia?.abuelos?.materno?.abuela ?? null,
        bisabuelo_p:    animal.genealogia?.bisabuelos?.paterno?.bisabuelo ?? null,
        bisabuela_p:    animal.genealogia?.bisabuelos?.paterno?.bisabuela ?? null,
        bisabuelo_m:    animal.genealogia?.bisabuelos?.materno?.bisabuelo ?? null,
        bisabuela_m:    animal.genealogia?.bisabuelos?.materno?.bisabuela ?? null,
      });
    } catch (error) {
      // El `navigate` guardado importa tanto como los setters: un request
      // viejo que falla sacaria al usuario de una ficha que SI cargo bien.
      if (estaCancelado()) return;
      message.error(error.response?.data?.error || 'Error al cargar animal');
      navigate('/animales');
    } finally {
      // Apagar el spinner desde el fetch cancelado mostraria el form a medio
      // llenar: el que manda ahora es el fetch nuevo, que ya lo prendio.
      if (!estaCancelado()) setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    setLoadingCategorias(true);
    try {
      const response = await categoriaService.getAll();
      setCategorias(response.data || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar categorías');
    } finally {
      setLoadingCategorias(false);
    }
  };

  const fetchCabanas = async () => {
    setLoadingCabanas(true);
    try {
      const response = await cabanaService.getActivas();
      const lista = response.data || [];
      setCabanas(lista);
      // UNA SOLA CABAÑA PROPIA = ya elegida, en el alta. Es lo que el backend
      // haria igual si el campo viniera vacio (`Cabana.unica_de`), asi que el
      // formulario lo MUESTRA en vez de hacerlo a escondidas: el productor ve
      // en que cabaña va a nacer el animal. Solo las propias cuentan —la
      // lista trae tambien las de mis invitadas— y solo en el alta: en la
      // edicion el valor es el del animal, que se carga aparte.
      const propias = lista.filter((c) => c.id_clientes === user?.id_clientes);
      if (!isEditing && propias.length === 1 && form.getFieldValue('id_cabana') == null) {
        form.setFieldValue('id_cabana', propias[0].id_cabana);
      }
    } catch (error) {
      // Mismo criterio que razas: sin aviso, el select queda vacio y no hay
      // forma de saber por que.
      message.error(error.response?.data?.error || 'Error al cargar cabañas');
    } finally {
      setLoadingCabanas(false);
    }
  };

  const fetchRazas = async () => {
    setLoadingRazas(true);
    try {
      const response = await razaService.getAll();
      setRazas(response.data || []);
    } catch (error) {
      // Sin aviso, el select de Raza queda vacío y no hay forma de saber por qué.
      message.error(error.response?.data?.error || 'Error al cargar razas');
    } finally {
      setLoadingRazas(false);
    }
  };

  const fetchEventos = async () => {
    setLoadingEventos(true);
    try {
      // getListaCorta y no getAll: el selector usa `id_evento` y
      // `nombre_evento`, y `getAll` traia los ~30 campos de cada evento
      // —imgsec_evento, mensaje_oferta_1/2 en HTML, categoria_evento— para
      // pintar un <Option>. Es el endpoint que ya usan los otros 12 selectores
      // de evento del front; esta pantalla era la unica que habia quedado. Los
      // dos filtran con `filtro_eventos_visibles`, asi que el alcance no cambia.
      //
      // El tope sigue existiendo —`lista-corta` corta en 200— pero AVISA,
      // igual que la navegacion entre animales y la lista de clientes. Cambiar
      // 100 por 200 sin el chequeo no arreglaba nada: solo movia el dia en que
      // el evento mas nuevo desaparece del selector sin decir una palabra.
      // El aviso es propio y no el generico porque esta pantalla SABE que
      // hacer con el remate que no entro: asignarlo desde el otro lado.
      const response = await eventoService.getListaCorta();
      setEventos(eventosDeListaCorta(
        response,
        'Hay más remates de los que entran en el selector. '
        + 'Si no encontrás el que buscás, asignalo desde la pantalla del remate.',
      ));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar eventos');
    } finally {
      setLoadingEventos(false);
    }
  };

  const fetchClientes = async () => {
    setLoadingClientes(true);
    try {
      // El `per_page` sigue siendo un tope, pero ahora es un tope QUE AVISA, y
      // esa es toda la diferencia. Este select lo usa el admin de plataforma al
      // dar de alta un animal, y ve TODOS los tenants de Puja: pasado el tope,
      // el cliente mas nuevo no aparecia y no habia ninguna señal — la misma
      // falla silenciosa que la navegacion entre animales y que el selector de
      // eventos. Subir el numero sin el chequeo solo mueve el dia del problema.
      const response = await clienteService.getAll({ per_page: 1000 });
      const { clientes: lista = [], total = 0 } = response.data || {};
      setClientes(lista);
      if (total > lista.length) {
        message.warning(
          'Hay más clientes de los que entran en el selector. '
          + 'Buscá el cliente desde la pantalla de Clientes.'
        );
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar clientes');
    } finally {
      setLoadingClientes(false);
    }
  };

  const onCategoriaChange = (idCategoria) => {
    const cat = categorias.find(c => c.id_categoria === idCategoria);
    setSexo(cat?.sexo ?? null);
  };

  // ── Guardado por modal ────────────────────────────────────────────────────
  // "Listo" persiste SOLO su bloque, y solo EDITANDO: en el alta todavía no
  // existe el animal, así que ahí el modal únicamente cierra y todo se guarda
  // junto con el form al final.
  const guardarBloque = async (campos, etiqueta, cerrar, guardar) => {
    if (!isEditing) {          // alta: lo guarda el submit del form completo
      cerrar();
      return;
    }
    setSaving(true);
    try {
      const values = form.getFieldsValue(campos);
      // MISMA normalizacion que onFinish, y por el mismo motivo: un campo con
      // allowClear queda en `undefined`, JSON.stringify borra esas claves, y
      // los dos backends recorren `if campo in data`
      // (animal_routes.py:298 para el animal, :438 para la genealogia). Sin
      // esto, vaciar un DEP desde el modal mostraba "DEPs guardado" y dejaba
      // el valor viejo en la base.
      //
      // Y lo peor no era que fallara, era que fallara INCONSISTENTE: el mismo
      // campo, vaciado igual, SI se borra si cerras el modal y apretas
      // "Guardar", porque ese camino pasa por onFinish. Dos botones, mismo
      // campo, resultados opuestos — y el que miente es el que dice "guardado".
      campos.forEach(k => { if (values[k] === undefined) values[k] = null; });
      await guardar(values);
      message.success(`${etiqueta} guardado`);
      cerrar();
    } catch (error) {
      // No se cierra el modal: así el dato editado no se pierde de vista.
      message.error(error.response?.data?.error || `Error al guardar ${etiqueta}`);
    } finally {
      setSaving(false);
    }
  };

  const guardarGrupoGenetico = (sufijo, etiqueta, cerrar) =>
    guardarBloque(
      FILAS_GENETICAS.map(({ key }) => `${key}_${sufijo}`),
      etiqueta,
      cerrar,
      (values) => animalService.update(id, values),
    );

  const guardarGenealogia = (cerrar) =>
    guardarBloque(
      GENEALOGIA_CAMPOS,
      'Genealogía',
      cerrar,
      (values) => animalService.saveGenealogia(id, values),
    );

  // El "Listo" de Servicio persistia NADA: solo cerraba el modal, mientras que
  // el de DEPs, Precision, Ranks y Genealogia guardaba y confirmaba. Mismo
  // boton, misma etiqueta, misma posicion, y en uno de los seis no pasaba nada.
  // El dato no se perdia —el submit del form lo mandaba igual— pero eso es
  // confiar en una costumbre, no una garantia.
  const guardarServicio = (cerrar) =>
    guardarBloque(
      SERVICIO_CAMPOS,
      'Servicio',
      cerrar,
      // Las fechas se formatean ACA por la misma razon que en onFinish: son
      // objetos dayjs, y `guardarBloque` ya dejo en null las que se vaciaron.
      (values) => animalService.update(id, {
        ...values,
        fecha_servicio: values.fecha_servicio?.trim() || null,
        fecha_parto: values.fecha_parto?.format('YYYY-MM-DD') ?? null,
      }),
    );

  // Sin esto el submit falla en silencio: antd solo marca el campo en rojo, y
  // si quedó fuera de la vista (o dentro de un modal cerrado) parece que el
  // botón Guardar no hizo nada. `scrollToFirstError` lleva hasta el campo.
  const onFinishFailed = ({ errorFields }) => {
    // EL RESET TAMBIEN VA ACA, y el `finally` de onFinish no alcanzaba: cuando
    // la validacion falla antd llama a ESTA funcion y onFinish no corre nunca,
    // asi que el ref quedaba en 'siguiente'.
    //
    // Apretas "Guardar y siguiente" con el nombre vacio -> error rojo, ref en
    // 'siguiente'. Completas el nombre, apretas Enter dentro del Input -> antd
    // dispara el submit implicito SIN pasar por ningun click -> se guarda y te
    // manda al animal siguiente. Pediste guardar y te mudaron de ficha.
    accionGuardarRef.current = 'quedarse';

    const campo = errorFields?.[0]?.name?.[0];
    const label = ETIQUETAS_CAMPOS[campo];
    message.error(
      label
        ? `Falta completar "${label}". Revisá los campos marcados en rojo.`
        : 'Hay campos obligatorios sin completar. Revisá los marcados en rojo.',
    );
  };

  const onFinish = async (values) => {
    setSaving(true);

    // Extraer campos de genealogía antes de enviar el animal
    const genealogiaData = {};
    GENEALOGIA_CAMPOS.forEach(k => { genealogiaData[k] = values[k] ?? null; });
    const tieneGenealogia = GENEALOGIA_CAMPOS.some(k => values[k]);

    // Data del animal (sin genealogía)
    const animalValues = { ...values };
    GENEALOGIA_CAMPOS.forEach(k => delete animalValues[k]);
    // Un Select con allowClear queda en `undefined`, JSON.stringify borra esas
    // claves y el backend hace `if campo in data`: la columna no se tocaba
    // nunca. Por eso no se podía vaciar visualizacion_animal (que según la
    // ayuda del campo debería heredar de la categoría), ni la raza, ni un
    // servicio cargado por error. `null` sí viaja.
    Object.keys(animalValues).forEach(k => {
      if (animalValues[k] === undefined) animalValues[k] = null;
    });

    const data = {
      ...animalValues,
      // El `?? null` no es decorativo: estas tres líneas corren DESPUÉS de la
      // normalización de arriba, así que sin él la deshacían. Un DatePicker
      // vaciado deja `undefined`, `?.format()` propaga `undefined`,
      // JSON.stringify borra la clave y el backend nunca limpia la columna
      // (`fecha_servicio` y `fecha_parto` están en CAMPOS_EDITABLES_ANIMAL).
      // El usuario veía "Animal actualizado" con la fecha vieja intacta.
      // `fecha_servicio` es texto libre (un período), no un DatePicker.
      fecha_nac: values.fecha_nac?.format('YYYY-MM-DD') ?? null,
      fecha_servicio: values.fecha_servicio?.trim() || null,
      fecha_parto: values.fecha_parto?.format('YYYY-MM-DD') ?? null,
      // OJO: va con spread condicional y NO como `id_evento: ... ?? null`.
      //
      // El campo se dibuja solo si `eventos.length > 0`. Si no se dibujo —sin
      // `ver_eventos`, o porque el fetch fallo— el Form.Item nunca se registra,
      // `values.id_evento` es `undefined`, y el `?? null` lo convertia en un
      // null EXPLICITO. `id_evento` esta en CAMPOS_EDITABLES_ANIMAL y el PUT
      // recorre `if campo in data`, asi que el animal SALIA DEL REMATE con el
      // cartel "Animal actualizado" en pantalla.
      //
      // `id_cabana` esta gateado igual y es seguro justamente porque NO tiene
      // una linea propia aca: su clave se ausenta y el backend no toca la
      // columna. Esto lo iguala.
      //
      // LA REGLA, generalizada: un Form.Item que puede no montarse —por Modal
      // sin forceRender O por render condicional— o no se lee de `values`, o no
      // se manda con `?? null`. Campo ausente no significa campo vacio.
      ...(eventos.length > 0 ? { id_evento: values.id_evento ?? null } : {}),
      // Truthiness, NO `=== false`: el backend devuelve mostrar_copa como
      // SmallInteger (0/1), nunca booleano, así que `0 === false` daba false y
      // se reenviaba 1. Editar cualquier campo de un animal con la copa
      // apagada se la volvía a prender sin que nadie lo tocara.
      mostrar_copa: values.mostrar_copa ? 1 : 0,
    };

    try {
      let animalId = id;

      if (isEditing) {
        await animalService.update(id, data);
      } else {
        const res = await animalService.create(data);
        animalId = res.data?.animal?.id_animal;
        // `animalId &&` igual que los dos bloques de abajo: si se lee con `?.`
        // porque no se confia del todo en la forma de la respuesta, se guarda
        // en los TRES usos o en ninguno. Sin esto, un `animalId` undefined
        // mandaba `POST /imagenes/upload-animal/undefined`.
        // `puedeFotos` tambien aca: sin el, el animal se crea, la subida
        // devuelve 403 y el productor recibe un aviso de algo que nunca podia
        // funcionar.
        if (animalId && pendingFiles.length && puedeFotos) {
          try {
            await imagenService.uploadAnimal(animalId, pendingFiles);
          } catch (err) {
            message.warning(err.response?.data?.error || 'Las fotos no se pudieron subir, pero el animal fue creado.');
          }
        }
      }

      // Genealogía: editando se envía SIEMPRE, para que vaciar un campo
      // también se persista. Antes solo se llamaba si había contenido, así que
      // borrar la genealogía no borraba nada y el cartel decía "actualizado".
      // En el alta solo se manda si hay algo, para no crear un registro vacío.
      if (animalId && (isEditing || tieneGenealogia)) {
        try {
          await animalService.saveGenealogia(animalId, genealogiaData);
        } catch (err) {
          message.warning(err.response?.data?.error || 'Animal guardado, pero hubo un error al guardar la genealogía.');
        }
      }

      message.success(isEditing ? 'Animal actualizado' : 'Animal creado');
      if (!isEditing) {
        navigate('/animales');               // al crear, volvemos al listado
      } else if (accionGuardarRef.current === 'siguiente' && vecinos.next) {
        navigate(`/animales/${vecinos.next}/editar`);  // guardar y pasar al siguiente
      }
      // 'quedarse' (default): no navega, sigue editando este animal
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
      // El reset va en el `finally` y no despues del navigate: el ref solo lo
      // escriben los onClick de los dos botones, pero Enter dentro de un Input
      // dispara el submit implicito de antd SIN pasar por ningun click. Si el
      // guardado fallaba con "Guardar y siguiente" apretado, el reset quedaba
      // del lado muerto del try: el usuario corregia el campo, apretaba Enter,
      // y lo mandaba al animal siguiente sin haberlo pedido.
      accionGuardarRef.current = 'quedarse';
    }
  };

  if (loading) {
    return (
      <PujaSpinner size="lg" />
    );
  }

  // El boton de subir, UNO solo: va en el header de la card «Fotos» cuando hay
  // fotos y en el cuerpo cuando no. Definido una vez para que las dos
  // posiciones no diverjan.
  //
  // MULTIPLE, y sin `maxCount`. Hasta el 17/9 este input subia de a una y el
  // unico camino para cargarle cinco fotos a un animal era la carga masiva, que
  // las asigna por el NOMBRE del archivo. Desde la ficha no hace falta adivinar
  // de quien es cada foto: ya estamos parados en el.
  //
  // `beforeUpload` se dispara UNA VEZ POR ARCHIVO, asi que seleccionar cinco
  // hace cinco llamadas. Se juntan en `handleUploadImagen`, que usa el segundo
  // argumento de antd —la tanda completa— y sale temprano en todas menos la
  // primera (`tanda.indexOf(file) > 0`). NO hay un buffer: antd ya entrega la
  // lista entera en cada llamada.
  const hayFotos = isEditing ? imagenes.length > 0 : pendingFiles.length > 0;
  const botonSubir = (
    <Upload
      accept="image/*"
      showUploadList={false}
      beforeUpload={handleUploadImagen}
      multiple
      disabled={!puedeFotos}
    >
      <Button
        icon={uploadingImg ? <LoadingOutlined /> : <PlusOutlined />}
        loading={uploadingImg}
        disabled={!puedeFotos}
      >
        {/* "Agregar" y NO "Cambiar": esto NO reemplaza nada, SUMA. El texto
            decia "Cambiar imagen" y el productor que queria otra portada subia
            una segunda creyendo que pisaba la anterior. Cual se publica se
            elige marcandola en su fila, no subiendo encima. */}
        {hayFotos ? 'Agregar fotos' : 'Subir fotos'}
      </Button>
    </Upload>
  );

  /* Saca ESTE animal de su remate, aunque esté en curso: lo decide el dueño
   * del animal o el organizador (mismo endpoint que el listado). Al volver,
   * el selector de remate —si está dibujado— queda vacío, para que un
   * «Guardar» posterior no lo vuelva a meter. */
  const handleSacarDelRemate = async () => {
    setSacando(true);
    try {
      const { data } = await animalService.retirarEvento([Number(id)]);
      message.success(`Sacado de ${remate?.nombre || 'su remate'}`);
      if (data.con_ofertas) {
        message.warning('Tenía ofertas en ese remate: quedan registradas, pero el animal ya no está en venta.');
      }
      setRemate(null);
      form.setFieldsValue({ id_evento: null });
    } catch (error) {
      message.error(error.response?.data?.error || 'No se pudo sacar el animal del remate');
    } finally {
      setSacando(false);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/animales')}>
            Volver
          </Button>
        </Col>
        <Col>
          <Space align="center" wrap>
            {/* Con `crud_animales`, que es lo que pide el endpoint: la cabaña
                invitada lo tiene y es la que más lo necesita (no tiene el
                selector de remate de más abajo). */}
            {isEditing && remate && tieneRecurso('crud_animales') && (
              <Popconfirm
                title="Sacar del remate"
                description={
                  `Vas a sacar este animal de «${remate.nombre || 'su remate'}», aunque esté en curso: ` +
                  'deja de verse en el micrositio y queda libre para otro remate. ' +
                  'Las ofertas y ventas ya registradas no se tocan.'
                }
                okText="Sacar"
                cancelText="Cancelar"
                onConfirm={handleSacarDelRemate}
              >
                <Button danger loading={sacando}>
                  Sacar de {remate.nombre ? `«${remate.nombre}»` : 'su remate'}
                </Button>
              </Popconfirm>
            )}
            <Title level={2} style={{ margin: 0 }}>
              {isEditing ? 'Editar Animal' : 'Nuevo Animal'}
            </Title>
          </Space>
        </Col>
      </Row>

      {/* Barra de datos genéticos */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '12px 16px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#555', marginRight: 8 }}>Datos genéticos:</span>
          <Button icon={<BarChartOutlined />} onClick={() => setDepsVisible(true)}>
            DEPs
          </Button>
          <Button icon={<ExperimentOutlined />} onClick={() => setPresVisible(true)}>
            Datos de Precisión
          </Button>
          <Button icon={<TrophyOutlined />} onClick={() => setRankVisible(true)}>
            Ranks
          </Button>
          <Button icon={<ApartmentOutlined />} onClick={() => setGenealVisible(true)}>
            Genealogía
          </Button>
          {sexo === 'F' && (
            <Button icon={<CalendarOutlined />} onClick={() => setServicioVisible(true)}>
              Servicio
            </Button>
          )}
          {/* Toda hembra puede tener crías: la visualización 3 ("Hembra con
              cría") no lleva servicio, así que condicionarlo a `tieneServicio`
              dejaba sin forma de cargarlas justo a las fichas que las muestran.
              Desde el 2026-10-21 son VARIAS por madre (las elecciones) y se
              cargan en planilla en la solapa «Crías» de la ficha, como los
              familiares: al crear todavía no hay id, así que sólo al editar. */}
          {sexo === 'F' && isEditing && (
            <Button
              icon={<HeartOutlined />}
              onClick={() => navigate(`/animales/${id}?tab=crias`)}
            >
              Crías <ArrowRightOutlined style={{ fontSize: 11 }} />
            </Button>
          )}
          {isEditing && (
            // Las vinculaciones a familiares se gestionan en la ficha
            // (mini-grilla inline) porque son relaciones con el catalogo,
            // no datos propios del animal. Al crear todavia no hay id, asi
            // que solo se muestra el boton en modo edicion.
            <Button
              icon={<UsergroupAddOutlined />}
              onClick={() => navigate(`/animales/${id}?tab=familiares`)}
            >
              Familiares <ArrowRightOutlined style={{ fontSize: 11 }} />
            </Button>
          )}
        </Space>

        {/* Navegación entre animales: solo flechas, alineadas a la derecha */}
        {isEditing && (
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={!vecinos.prev}
              onClick={() => vecinos.prev && navigate(`/animales/${vecinos.prev}/editar`)}
              title="Animal anterior"
            />
            <Button
              icon={<ArrowRightOutlined />}
              disabled={!vecinos.next}
              onClick={() => vecinos.next && navigate(`/animales/${vecinos.next}/editar`)}
              title="Animal siguiente"
            />
          </Space>
        )}
        </div>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        scrollToFirstError
        initialValues={{
          // 'registro' ya no trae default: el valor depende de la raza y la
          // asociación, y precargar uno induce a guardarlo equivocado.
          mostrar_copa: true,
          // LISTA VACIA Y NO undefined, o el ALTA se rompe entera. El
          // `Form.Item` de características está siempre montado, así que
          // rc-field-form emite la clave aunque nadie la toque; el loop de más
          // arriba convierte ese `undefined` en `null`, y `null` en una columna
          // `jsonb NOT NULL` con `CHECK (jsonb_typeof = 'array')` es una
          // CheckViolation. SQLAlchemy no salva: el tipo JSONB tiene
          // `should_evaluate_none = True`, así que NO cae al `default=list` —
          // incluye la columna en el INSERT con el literal JSON `null`.
          caracteristicas: [],
        }}
      >
        <Row gutter={24}>
          {/* Columna izquierda */}
          <Col xs={24} lg={12}>
            <Card title="Datos Básicos">
              <Row gutter={16}>
                <Col xs={24} sm={16}>
                  <Form.Item
                    name="nom_animal"
                    label="Nombre"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <Input placeholder="Nombre del animal" />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <NombreCortoField form={form} />
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="rp_animal"
                    label="RP"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <Input placeholder="RP" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="corral"
                    label="Corral"
                  >
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="cantidad_corral"
                    label="Cant. Corral"
                  >
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  {/* Texto libre, no select: los tipos de registro varían por
                      raza y por asociación, y una lista cerrada siempre queda
                      corta (el select ofrecía PC/PP, que no usa ningún animal
                      cargado; los reales son D, R, C, A y otros).

                      PERO libre hasta 2 CARACTERES, que es lo que la columna
                      acepta (`registro` es String(2), `reg_cria` es CHAR(2)).
                      Sin el tope, escribir tres letras terminaba en un
                      `StringDataRightTruncation` que animal_routes devuelve
                      como `str(e)` en un 500: el usuario recibia un volcado de
                      psycopg2 en un toast rojo despues de cargar la ficha
                      entera. Prometer texto libre y guardar en 2 chars es la
                      promesa que rompe la BD, no la UI. */}
                  <Form.Item
                    name="registro"
                    label="Registro"
                    rules={[
                      { required: true, message: 'Requerido' },
                      { max: 2, message: 'Máximo 2 caracteres' },
                    ]}
                  >
                    <Input maxLength={2} placeholder="Ej: D, R, C, A, PP" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="fecha_nac"
                    label="Fecha Nacimiento"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="id_categoria" label="Categoría">
                    <Select placeholder="Seleccionar categoría" showSearch optionFilterProp="children" onChange={onCategoriaChange} loading={loadingCategorias}>
                      {categorias.map(cat => (
                        <Option key={cat.id_categoria} value={cat.id_categoria}>
                          {cat.nom_categoria}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="id_raza" label="Raza">
                    <Select placeholder="Seleccionar raza" showSearch optionFilterProp="children" allowClear loading={loadingRazas}>
                      {razas.map(r => (
                        <Option key={r.id_raza} value={r.id_raza}>
                          {r.nom_raza}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Sólo aparece si el cliente tiene cabañas cargadas: en un remate
                  de una sola cabaña este campo es ruido. La cabaña es de dónde
                  VIENE el animal — el dueño sigue siendo el tenant. */}
              {cabanas.length > 0 && (
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="id_cabana"
                      label="Cabaña"
                      /* Decia "para remates con varias cabañas participantes",
                         y no: la cabaña es la puerta al catalogo de genetica
                         —sin ella el animal no se puede publicar— y en un
                         remate dice de donde viene. Con una sola cabaña ya
                         viene elegida (ver el efecto de `cabanas`). */
                      extra="Sin cabaña, el animal no entra al catálogo de genética"
                    >
                      <Select
                        placeholder="Sin cabaña asignada"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        loading={loadingCabanas}
                      >
                        {/* `nombre_publico` primero, igual que
                            `Animal.resolver_procedencia()`. Es el nombre que
                            termina impreso en el catalogo, el PDF y el Excel;
                            mostrar `nom_cabana` aca hacia que el productor
                            eligiera "Santa Juana" y al comprador le llegara
                            "Cabaña Santa Juana S.A." — misma cabaña, dos
                            nombres, y el que la asigno no reconoce el suyo. */}
                        {cabanas.map(c => (
                          <Option key={c.id_cabana} value={c.id_cabana}>
                            {c.nombre_publico || c.nom_cabana}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="visualizacion_animal"
                    label="Visualización (override)"
                    extra="Dejá en blanco para heredar la visualización de la categoría"
                  >
                    <Select placeholder="Heredar de categoría" allowClear>
                      <Option value={1}>🐂 Toro</Option>
                      <Option value={2}>🐄 Hembra con servicio</Option>
                      <Option value={3}>🐄 Hembra con cría</Option>
                      <Option value={4}>🐄 Hembra básica</Option>
                      <Option value={5}>🐄 Hembra parida con servicio</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                {/* Mismo criterio que la Cabaña de abajo: si la lista vino
                    vacia el campo no se dibuja. Sin `ver_eventos` el fetch no
                    corre, `eventos` queda [], y un animal que YA tiene evento
                    hacia que antd no encontrara el <Option> y pintara el id
                    pelado — un "7" donde va el nombre del remate. Gatear el
                    fetch y dejar el campo era medio gate. */}
                {eventos.length > 0 && (
                  <Col xs={24} sm={12}>
                    <Form.Item name="id_evento" label="Evento">
                      <Select placeholder="Seleccionar evento" allowClear loading={loadingEventos}>
                        {eventos.map(ev => (
                          <Option key={ev.id_evento} value={ev.id_evento}>
                            {ev.nombre_evento}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                {/* SOLO el admin de plataforma, y no es cosmetico.
                    `is_admin()` mira el rol a secas, asi que un rol 1 CON
                    id_clientes —que es un admin de CABAÑA, o sea un cliente—
                    pasaba por dos agujeros:
                    1. El desplegable le listaba TODOS los tenants de Puja. Es
                       el directorio recorrible que /clientes/buscar-por-cuit
                       existe para impedir, entrando por otra puerta.
                    2. En el alta, dejarlo vacio mandaba `id_clientes: null`, y
                       `crear_animal` solo pisa el valor si `is_productor()`. La
                       columna es nullable => animal con dueño NULL, invisible
                       para siempre porque `tenant_scoped` filtra por ahi. Con
                       el cartel "Animal creado" en pantalla.
                    Si el campo no se muestra, no se registra, no viaja, y el
                    backend resuelve el dueño solo. */}
                {esAdminPlataforma(user) && (
                  <Col xs={24} sm={12}>
                    {/* El backend NO permite reasignar el dueño (id_clientes queda
                        fuera de CAMPOS_EDITABLES_ANIMAL para evitar cross-tenant),
                        así que editando se muestra deshabilitado: un control que
                        la API no honra hace creer que el cambio se guardó. */}
                    {/* `required` al crear: el Select tiene allowClear, y
                        dejarlo vacio mandaba `id_clientes: null` explicito por
                        la normalizacion de onFinish. El backend ahora lo
                        rechaza con 400 —esa es la barrera de verdad— pero
                        avisar antes de mandar es mejor que un error rojo
                        despues de completar toda la ficha. */}
                    <Form.Item
                      name="id_clientes"
                      label="Cliente"
                      rules={!isEditing ? [{ required: true, message: 'Elegí el cliente dueño del animal' }] : undefined}
                      extra={clienteBloqueado ? 'El cliente no se puede cambiar una vez creado el animal.' : undefined}
                    >
                      <Select
                        placeholder="Seleccionar cliente"
                        allowClear
                        loading={loadingClientes}
                        disabled={clienteBloqueado}
                      >
                        {clientes.map(cl => (
                          <Option key={cl.id_clientes} value={cl.id_clientes}>
                            {cl.razon_social}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Card>

            <Card title="Identificación" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="caravana" label="Caravana">
                    <Input maxLength={50} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="hba_animal" label="HBA">
                    <Input maxLength={50} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="adn_animal" label="ADN">
                    <Input maxLength={50} />
                  </Form.Item>
                </Col>
              </Row>

              {/* CO_PROPIETARIO EXISTIA HACE RATO Y NO TENIA DONDE CARGARSE:
                  esta en la base y en la lista de campos editables de la API,
                  pero el formulario nunca lo mostro. Por eso esta vacio en los
                  641 animales. La ficha publica ahora lo muestra al lado de la
                  cabaña, asi que sin este campo no iba a tener dato nunca. */}
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="co_propietario"
                    label="Copropietario"
                    tooltip="Se muestra en la ficha pública junto a la cabaña. En genética de alto valor la copropiedad es información contractual."
                  >
                    <Input maxLength={255} placeholder="Ej: Grupo El Álamo" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="centro_genetico"
                    label="Centro genético"
                    tooltip="Dónde está alojado el reproductor. No entra por la carga masiva: se completa acá, animal por animal."
                  >
                    <Input maxLength={255} placeholder="Ej: CIALE Alta" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Columna derecha */}
          <Col xs={24} lg={12}>
            <Card title="Características Físicas">
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  {/* `maxLength` con el largo REAL de la columna. Vale doble
                      en `cabeza`: `normalizar_cabeza()` devuelve tal cual lo
                      que no reconoce —el vocabulario quedo abierto a proposito—
                      asi que el sistema invita a escribir libre en 11
                      caracteres. Sin el tope, "Espejuelos ch" es un
                      StringDataRightTruncation que sale como un 500 con el
                      volcado de psycopg2 en un toast, despues de que el
                      productor lleno la ficha entera. Mismo argumento que ya
                      esta escrito para `registro`. */}
                  <Form.Item name="cabeza" label="Cabeza">
                    <Input maxLength={11} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="pelo" label="Pelo">
                    <Input maxLength={11} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="frame" label="Frame">
                    <InputNumber style={{ width: '100%' }} step={0.1} />
                  </Form.Item>
                </Col>
              </Row>

              {/* LOS CUATRO PESOS JUNTOS Y EN ORDEN DE LA CURVA DE
                  CRECIMIENTO: nacimiento, destete, 18 meses, hoy. Es como los
                  lee un cabañero y como los tiene en su planilla, asi que
                  cargarlos salteados invita a equivocarse de campo. `ce_actual`
                  bajo a la fila de las otras medidas, que es a lo que se
                  parece. */}
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="peso_nacer" label="Peso al Nacer (kg)">
                    <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="peso_destete" label="Peso al Destete (kg)">
                    <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="peso_18m" label="Peso a 18 Meses (kg)">
                    <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="peso_actual" label="Peso Actual (kg)">
                    <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="ce_actual" label="CE Actual">
                    <InputNumber style={{ width: '100%' }} step={0.1} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="aob_actual" label="AOB Actual">
                    <InputNumber style={{ width: '100%' }} step={0.1} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="egd_actual" label="EGD Actual">
                    <InputNumber style={{ width: '100%' }} step={0.1} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="gi_actual" label="%GI Actual">
                    <InputNumber style={{ width: '100%' }} step={0.1} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  {/* Se carga a mano y no se calcula: las crias reales del
                      reproductor incluyen animales que nunca pasaron por Puja
                      (vendidos, otros establecimientos, años anteriores). */}
                  <Form.Item
                    name="cantidad_crias"
                    label="Cantidad de crías"
                    tooltip="Crías registradas del reproductor. Se muestra en el catálogo de genética."
                  >
                    <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  {/* Sin el año, el RANK de arriba queda sin referencia: las
                      asociaciones recalculan todos los años y el mismo animal
                      cambia de valores entre sumarios. */}
                  <Form.Item
                    name="deps_evaluacion_anio"
                    label="Año del sumario"
                    tooltip="De qué evaluación de la asociación salieron los DEPs y los RANK. Si no lo cargás, se usa el año de alta."
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1990}
                      max={new Date().getFullYear() + 1}
                      precision={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Precio y Oferta" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="precio_base" label="Precio Base ($)">
                    <InputNumber 
                      style={{ width: '100%' }} 
                      min={0}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => (value ?? '').replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="salto_oferta" label="Salto Oferta ($)">
                    <InputNumber 
                      style={{ width: '100%' }} 
                      min={0}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => (value ?? '').replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Información Adicional" style={{ marginTop: 16 }}>
              <Form.Item name="mensaje" label="Mensaje">
                <TextArea rows={2} placeholder="Mensaje para mostrar" />
              </Form.Item>

              {/* DESCRIPCION Y MENSAJE SON DOS COSAS DISTINTAS, y estan
                  pegados a proposito para que se vea la diferencia al cargar:
                  `mensaje` es el anuncio comercial de condicion de venta
                  ("SALE A VENTA EL 50%"), `descripcion` es el relato del
                  animal. Por eso no se reuso `mensaje` para esto: ya tenia
                  trabajo, y ademas es String(255). */}
              <Form.Item
                name="descripcion"
                label="Descripción"
                tooltip="Texto libre que se muestra debajo de las fotos en la ficha pública. Distinto del Mensaje, que es la condición de venta."
              >
                <TextArea rows={4} placeholder="Historia, estructura, linaje, lo que quieras contar del animal" />
              </Form.Item>

              {/* CARACTERISTICAS DESTACADAS: una LISTA, no un campo de texto.
                  Un animal puede tener varias —"Facilidad de parto", "Nueva
                  incorporacion", "Apto vaquillonas 18 meses"— y con un solo
                  string terminan concatenadas con comas, que es exactamente el
                  problema que tiene `premio` hoy: 7 de 9 animales publicados
                  guardan ahi una etiqueta comercial porque no habia otro lugar.
                  `mode="tags"` deja escribir libremente ademas de elegir: las
                  sugerencias son un atajo, no una lista cerrada. */}
              <Form.Item
                name="caracteristicas"
                label="Características destacadas"
                tooltip="Se muestran como etiquetas en la ficha pública. Escribí y apretá Enter para agregar."
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Escribí y Enter. Ej: Facilidad de parto"
                  tokenSeparators={[',']}
                  options={[
                    { value: 'Facilidad de parto' },
                    { value: 'Nueva incorporación' },
                    { value: 'Apto vaquillonas 18 meses' },
                    { value: 'Apto vaquillonas 24 meses' },
                  ]}
                />
              </Form.Item>

              <Form.Item name="premio" label="Premio" style={{ marginBottom: 4 }}>
                <Input placeholder="Premios obtenidos" />
              </Form.Item>
              <Form.Item name="mostrar_copa" valuePropName="checked" style={{ marginBottom: 16, textAlign: 'right' }}>
                <Checkbox>Muestra copa 🏆</Checkbox>
              </Form.Item>

              <Form.Item name="codigo_video" label="URL Video">
                <Input placeholder="https://youtube.com/..." />
              </Form.Item>
            </Card>

            {/* LA CARD LISTA TODAS LAS FOTOS, no "la principal". Con varias
                fotos por animal, una card de una sola con un tachito al lado
                borra la portada y promueve a la siguiente sin que el productor
                haya visto las otras. Cada fila trae lo que se le puede hacer a
                ESA foto: verla en grande (la miniatura), su presentacion
                (portada, carrusel, encuadre — los mismos controles que el
                catalogo, importados y no copiados) y borrarla.

                "Agregar fotos" va en el HEADER de la card y no en una fila:
                es una accion sobre la coleccion, no sobre una foto. Solo baja
                al cuerpo cuando no hay ninguna, porque un header con boton
                sobre un cuerpo vacio deja al productor buscando donde esta. */}
            <Card
              title="Fotos"
              style={{ marginTop: 16 }}
              extra={hayFotos ? botonSubir : null}
            >
              {isEditing && imagenes.length > 0 && (
                <FotosAnimal
                  imagenes={imagenes}
                  onCambio={handleCambioImagen}
                  // Borrar y presentar escriben con `fotos_videos`: sin el
                  // recurso no se ofrecen, igual que el boton de subir.
                  onEliminar={puedeFotos ? handleEliminarImagen : undefined}
                  conPresentacion={puedeFotos && puedeCatalogo}
                  eliminando={eliminandoImg}
                />
              )}
              {!isEditing && pendingFiles.length > 0 && (
                <FotosPendientes archivos={pendingFiles} onQuitar={quitarPendiente} />
              )}
              {!hayFotos && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  {botonSubir}
                </div>
              )}
              <div style={{ marginTop: 10, color: '#888', fontSize: 12 }}>
                {!puedeFotos ? (
                  'Tu plan no incluye la gestión de fotos.'
                ) : isEditing ? (
                  <>
                    {/* `_n` al final: el backend guarda
                        {rp}_{timestamp}_{indice}.ext desde que acepta varias
                        de una. El indice no es cosmetico — sin el, dos fotos
                        del mismo request se pisan el archivo. */}
                    Se guardará como <strong>{form.getFieldValue('rp_animal') || 'RP'}</strong>_timestamp_n.ext.
                    {/* Que dice la pista depende de que controles VE: a quien
                        tiene catalogo se le explica que son los switches; a
                        quien no, cual foto se publica, porque no tiene con
                        que elegirla. Y solo cuando HAY fotos: explicar los
                        switches de una lista vacia es hablar de algo que no
                        esta en pantalla. */}
                    {!hayFotos ? null : puedeCatalogo ? (
                      <> Marcá la <strong>portada</strong>, cuáles van al carrusel
                        y el encuadre de cada una: son los mismos controles que
                        la columna «Presentación» del catálogo de genética.
                      </>
                    ) : (
                      <> Se publica la marcada como <strong>portada</strong>, y si
                        no hay ninguna, la primera.
                      </>
                    )}
                  </>
                ) : (
                  'Las fotos se subirán automáticamente al crear el animal.'
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Botones */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Col>
            <Button onClick={() => navigate('/animales')} style={{ marginRight: 8 }}>
              {isEditing ? 'Volver al listado' : 'Cancelar'}
            </Button>
            {isEditing ? (
              <>
                <Button
                  htmlType="submit"
                  loading={saving}
                  icon={<SaveOutlined />}
                  onClick={() => { accionGuardarRef.current = 'quedarse'; }}
                  style={{ marginRight: 8 }}
                >
                  Guardar
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  disabled={!vecinos.next}
                  onClick={() => { accionGuardarRef.current = 'siguiente'; }}
                >
                  Guardar y siguiente <ArrowRightOutlined />
                </Button>
              </>
            ) : (
              <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                Crear Animal
              </Button>
            )}
          </Col>
        </Row>

        {/* LOS SEIS MODALES LLEVAN `forceRender`, Y NO ES COSMETICO.
            Sin el, antd no monta los hijos del Modal hasta la primera apertura
            (@rc-component/portal devuelve null mientras `open` es false). Los
            Form.Item de adentro NUNCA se registran, y `onFinish` arma su payload
            SOLO con los campos registrados: `form.setFieldsValue()` escribe en
            el store, pero el store no es lo que llega a `values`.
            Resultado: editabas el peso de un animal, guardabas sin abrir el
            modal de Genealogia, y los 10 campos viajaban como `?? null`
            explicitos. El backend recorre `if campo in data`, asi que la clave
            estaba con valor None y los ponia en NULL — padre, madre, 4 abuelos
            y 4 bisabuelos, con el cartel "Animal actualizado" en pantalla.
            Lo mismo con las fechas del servicio.
            Y era INTERMITENTE: si en esa sesion ya habias abierto el modal, los
            campos quedaban montados y todo andaba bien.
            Montar siempre ataca la causa. De paso arregla `tieneGenealogia`,
            que daba false siempre por el mismo motivo.
            REGLA: si un Form.Item vive dentro de un Modal, o el Modal lleva
            `forceRender`, o no lo leas desde el `values` de `onFinish`. */}
        {/* Modal DEPs */}
        <Modal
          title={<span><BarChartOutlined style={{ marginRight: 8 }} />DEPs — Diferencias Esperadas de Progenie</span>}
          open={depsVisible}
          forceRender
          onCancel={() => setDepsVisible(false)}
          onOk={() => guardarGrupoGenetico('deps', 'DEPs', () => setDepsVisible(false))}
          confirmLoading={saving}
          okText="Listo"
          cancelText="Cerrar"
          width={520}
        >
          <GrupoFields sufijo="deps" />
        </Modal>

        {/* Modal Precisión */}
        <Modal
          title={<span><ExperimentOutlined style={{ marginRight: 8 }} />Datos de Precisión</span>}
          open={presVisible}
          forceRender
          onCancel={() => setPresVisible(false)}
          onOk={() => guardarGrupoGenetico('pres', 'Precisión', () => setPresVisible(false))}
          confirmLoading={saving}
          okText="Listo"
          cancelText="Cerrar"
          width={520}
        >
          <GrupoFields sufijo="pres" />
        </Modal>

        {/* Modal Ranks */}
        <Modal
          title={<span><TrophyOutlined style={{ marginRight: 8 }} />Ranks</span>}
          open={rankVisible}
          forceRender
          onCancel={() => setRankVisible(false)}
          onOk={() => guardarGrupoGenetico('rank', 'Ranks', () => setRankVisible(false))}
          confirmLoading={saving}
          okText="Listo"
          cancelText="Cerrar"
          width={520}
        >
          <GrupoFields sufijo="rank" />
        </Modal>

        {/* Modal Genealogía */}
        <Modal
          title={<span><ApartmentOutlined style={{ marginRight: 8 }} />Genealogía / Pedigree</span>}
          open={genealVisible}
          forceRender
          onCancel={() => setGenealVisible(false)}
          onOk={() => guardarGenealogia(() => setGenealVisible(false))}
          confirmLoading={saving}
          okText="Listo"
          cancelText="Cerrar"
          width={660}
        >
          <GenealogiaFields />
        </Modal>

        {/* Modal Servicio */}
        <Modal
          title={<span><CalendarOutlined style={{ marginRight: 8 }} />Datos de Servicio</span>}
          open={servicioVisible}
          forceRender
          onCancel={() => setServicioVisible(false)}
          onOk={() => guardarServicio(() => setServicioVisible(false))}
          confirmLoading={saving}
          okText="Listo"
          cancelText="Cerrar"
          width={520}
        >
          <ServicioFields />
        </Modal>

      </Form>
    </div>
  );
};

// ── Campos de genealogía ──────────────────────────────────────────────
const GenealogiaFields = () => (
  <>
    <Divider orientation="left" plain style={{ fontSize: 12, color: '#888', marginTop: 0 }}>Padres</Divider>
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item name="nom_padre" label="Padre">
          <Input placeholder="Nombre del padre" />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="nom_madre" label="Madre">
          <Input placeholder="Nombre de la madre" />
        </Form.Item>
      </Col>
    </Row>

    <Divider orientation="left" plain style={{ fontSize: 12, color: '#888' }}>Abuelos Paternos</Divider>
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item name="abuelo_p" label="Abuelo">
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="abuela_p" label="Abuela">
          <Input />
        </Form.Item>
      </Col>
    </Row>

    <Divider orientation="left" plain style={{ fontSize: 12, color: '#888' }}>Abuelos Maternos</Divider>
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item name="abuelo_m" label="Abuelo">
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="abuela_m" label="Abuela">
          <Input />
        </Form.Item>
      </Col>
    </Row>

    <Divider orientation="left" plain style={{ fontSize: 12, color: '#888' }}>Bisabuelos Paternos</Divider>
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item name="bisabuelo_p" label="Bisabuelo">
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="bisabuela_p" label="Bisabuela">
          <Input />
        </Form.Item>
      </Col>
    </Row>

    <Divider orientation="left" plain style={{ fontSize: 12, color: '#888' }}>Bisabuelos Maternos</Divider>
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item name="bisabuelo_m" label="Bisabuelo">
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="bisabuela_m" label="Bisabuela">
          <Input />
        </Form.Item>
      </Col>
    </Row>
  </>
);

// ── Campos de servicio — solo para hembras ───────────────────────────
const ServicioFields = () => (
  <>
    <Row gutter={16} style={{ marginTop: 8 }}>
      <Col xs={24} sm={12}>
        <Form.Item name="tipo_servicio" label="Tipo de servicio">
          <Select placeholder="Seleccionar" allowClear>
            <Option value="IA">IA — Inseminación Artificial</Option>
            <Option value="IATF">IATF — IA a Tiempo Fijo</Option>
            <Option value="Monta Natural">Monta Natural</Option>
            <Option value="TE">TE — Transferencia Embrionaria</Option>
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="toro_servicio" label="Toro utilizado">
          <Input placeholder="Nombre del toro" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col xs={24} sm={12}>
        {/* Texto y no DatePicker: el servicio a campo es un PERÍODO y la
            cabaña lo escribe como lo escribe en la planilla. Se guarda y se
            muestra tal cual (2026-10-16). */}
        <Form.Item name="fecha_servicio" label="Fecha o período de servicio">
          <Input placeholder="Ej.: 01/06/2026 al 14/08/2026" maxLength={100} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item name="fecha_parto" label="Fecha de parto esperada">
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={24}>
        <Form.Item name="estado_servicio" label="Estado de preñez">
          <Select placeholder="Seleccionar estado" allowClear>
            <Option value="Preñada confirmada">Preñada confirmada</Option>
            <Option value="Preñada dudosa">Preñada dudosa</Option>
            <Option value="Vacía">Vacía</Option>
            <Option value="Pendiente diagnóstico">Pendiente diagnóstico</Option>
          </Select>
        </Form.Item>
      </Col>
    </Row>
  </>
);

const GrupoFields = ({ sufijo }) => (
  <Row gutter={16} style={{ marginTop: 8 }}>
    {FILAS_GENETICAS.map(({ key, label }) => (
      <Col xs={24} sm={12} key={key}>
        <Form.Item name={`${key}_${sufijo}`} label={label}>
          <InputNumber style={{ width: '100%' }} step={0.01} />
        </Form.Item>
      </Col>
    ))}
  </Row>
);

export default AnimalForm;
